import React, { useEffect, useRef, useState } from 'react';
import { Button, Loading, message } from '@tencent/spaui';
import { useAppDispatch, useTypedSelector } from 'store/index';
import { fetchAppWxList } from 'store/application';
import { post } from 'store/request';
import useField from 'pages/wechat-oauth/useField';
import UserContainer from 'components/user-container';
import { report } from 'store/report';
import './index.scss';
import Bottom from 'components/auth/bottom';

const appIdRegex = /^wx(?=.*\d)(?=.*[a-z])[\da-z]{16}$/;

const phoneRegex = /^1\d{10}$/;

const codeRegex = /^\d{6}$/;

const WechatOAuth: React.FC = () => {
  const {
    user: { user },
    application: { appWxInfo, appWxInfoLoading },
  } = useTypedSelector((state) => state);
  const dispatch = useAppDispatch();

  const params = new URLSearchParams(location.search);
  const clientId = params.get('client_id');

  useEffect(() => {
    if (user) {
      dispatch(fetchAppWxList(clientId));
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [user]);

  const [defaultOrganize, setDefaultOrganize] = useState<string>();
  const [defaultAppId, setDefaultAppId] = useState<string>();
  const [codeDisabled, setCodeDisabled] = useState<boolean>(true);
  const [countdown, setCountdown] = useState<number>();
  const interval = useRef<ReturnType<typeof setInterval>>();
  const [loading, setLoading] = useState<boolean>();

  const organize = useField({ defaultValue: defaultOrganize, label: '公司名称', required: true });
  const appId = useField({
    tag: 'textarea',
    defaultValue: defaultAppId,
    label: '微信公众号AppID',
    tooltip: '将用于私域工具的微信公众号绑定；默认选取当前广告账号关联的微信公众号（若有），可修改补充，多个请换行分隔',
    regex: appIdRegex,
  });
  const phone = useField({
    label: '手机号',
    tooltip: '用于该工具的账户登录，若未曾注册将自动注册生成新账户（私域工具与腾讯广告账户暂不直接互通）',
    required: true,
    regex: phoneRegex,
    onChange: (value) => setCodeDisabled(!phoneRegex.test(value)),
  });
  const code = useField({
    label: '验证码',
    required: true,
    regex: codeRegex,
    disabled: codeDisabled,
    extra: (
      <Button
        displayType="link"
        disabled={codeDisabled || !!countdown}
        onClick={() => {
          if ((window as any).TencentCaptcha) {
            try {
              // 直接生成一个验证码对象
              const captcha1 = new (window as any).TencentCaptcha(
                '2075023792',
                (res: { ret: number; ticket: string; randstr: string }) => {
                  if (res.ret !== 0) {
                    return;
                  }
                  post('/tof/sms', {
                    phone: phone.value,
                    ticket: res.ticket,
                    randstr: res.randstr,
                  }).then(() => {
                    message.success('验证码发送成功');
                    setCountdown(60);
                    interval.current = setInterval(() => {
                      setCountdown((value) => value - 1);
                    }, 1000);
                  });
                }
              );
              captcha1.show(); // 显示验证码
            } catch (error) {
              console.log(error);
            }
          }
        }}
      >
        {countdown ? `重新获取(${countdown}s)` : '获取验证码'}
      </Button>
    ),
  });

  const handleAuth = () => {
    setLoading(true);
    const params = {
      client_id: clientId,
      phone: phone.value,
      code: code.value,
      wx_app_ids: appId.value.split('\n').filter((line, index, arr) => line && arr.indexOf(line) === index),
      organize: organize.value,
    };
    post('/private_app/woa_redirect', params)
      .then((res) => {
        report({
          event: 'request-woa-redirect',
          client_id: clientId,
          url: res.redirect_uri,
          payload: JSON.stringify(params),
        });
        location.href = res.redirect_uri;
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    setDefaultAppId(appWxInfo.list.join('\n'));
    setDefaultOrganize(appWxInfo.organize);
  }, [appWxInfo]);

  useEffect(() => {
    if (countdown <= 0) {
      clearInterval(interval.current);
      setCodeDisabled(false);
    }
  }, [countdown]);

  return (
    <div className="wechat-oauth-page">
      <div className="header">
        <UserContainer toggle />
      </div>

      <div className="container">
        <div className="text-bold">私域工具快速登录</div>
        <div className="content">
          <Loading show={appWxInfoLoading} />
          {organize.element}
          {appId.element}
          {phone.element}
          {code.element}

          <Bottom
            id="wechat-oauth"
            clientId={clientId}
            licenses={[{ url: '/license01.pdf', track: 'link-license', label: '腾讯广告营销服务市场用户服务协议' }]}
            loading={loading}
            disabled={
              loading ||
              !organize.value ||
              !phone.value ||
              !code.value ||
              !!organize.error ||
              !!phone.error ||
              !!code.error
            }
            btnLabel="确认"
            onConfirm={handleAuth}
          />
        </div>
      </div>
    </div>
  );
};

export default WechatOAuth;
